import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import KPICards from "@/components/KPICards";
import TemperatureChart from "@/components/TemperatureChart";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import AlertHistory from "@/components/AlertHistory";
import DriverBehavior from "@/components/DriverBehavior";
import DeviceMap from "@/components/DeviceMap";
import DateRangeFilter from "@/components/DateRangeFilter";
import SealIntegrityTimeline from "@/components/SealIntegrityTimeline";
import DevicePanel from "@/components/DevicePanel";
import { RiskPredictionPanel, RiskBadgeInline } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { FileArrowDown } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

async function downloadReport(url, fallbackName) {
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` },
    responseType: "blob",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(response.data);
  link.download = fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}

function parseEventDate(event) {
  const d = event.date;
  if (!d) return null;
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
}

export default function Dashboard({ datasetId, datasetName, initialDevice }) {
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedDevice, setSelectedDevice] = useState(initialDevice || null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const devParam = selectedDevice ? `&device_name=${encodeURIComponent(selectedDevice)}` : '';
      const [summaryRes, trendsRes, alertsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?dataset_id=${datasetId}${devParam}`),
        axios.get(`${API}/analytics/trends?dataset_id=${datasetId}${devParam}`),
        axios.get(`${API}/alerts?dataset_id=${datasetId}`),
        axios.get(`${API}/events?dataset_id=${datasetId}${devParam}`),
      ]);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data);
      setAlerts(alertsRes.data);
      setEvents(eventsRes.data);
    } catch (e) {
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, [datasetId, selectedDevice]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter events/trends by date range
  const filteredEvents = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return events;
    return events.filter((e) => {
      const d = parseEventDate(e);
      if (!d) return true;
      if (dateRange.from) {
        const from = new Date(dateRange.from);
        from.setHours(0, 0, 0, 0);
        if (d < from) return false;
      }
      if (dateRange.to) {
        const to = new Date(dateRange.to);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      return true;
    });
  }, [events, dateRange]);

  const filteredTrends = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return trends;
    return trends.filter((t) => {
      const ts = t.timestamp || "";
      const datePart = ts.split(" ")[0];
      if (!datePart) return true;
      const d = new Date(datePart);
      if (isNaN(d.getTime())) return true;
      if (dateRange.from) {
        const from = new Date(dateRange.from);
        from.setHours(0, 0, 0, 0);
        if (d < from) return false;
      }
      if (dateRange.to) {
        const to = new Date(dateRange.to);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      return true;
    });
  }, [trends, dateRange]);

  const filteredAlerts = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return alerts;
    return alerts.filter((a) => {
      const ts = a.timestamp || "";
      const datePart = ts.split(" ")[0];
      if (!datePart) return true;
      const d = new Date(datePart);
      if (isNaN(d.getTime())) return true;
      if (dateRange.from) {
        const from = new Date(dateRange.from);
        from.setHours(0, 0, 0, 0);
        if (d < from) return false;
      }
      if (dateRange.to) {
        const to = new Date(dateRange.to);
        to.setHours(23, 59, 59, 999);
        if (d > to) return false;
      }
      return true;
    });
  }, [alerts, dateRange]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-teal-400 font-mono text-sm">
          Loading analytics...
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard" className="flex gap-4 md:gap-6">
      {/* Device Panel - Left */}
      <div className="hidden lg:block w-[280px] shrink-0">
        <div className="sticky top-[60px]">
          <DevicePanel
            datasetId={datasetId}
            selectedDevice={selectedDevice}
            onSelectDevice={setSelectedDevice}
          />
        </div>
      </div>

      {/* Main Content - Right */}
      <div className="flex-1 min-w-0 space-y-4 md:space-y-6">
        {/* Dataset info + Date Range + Device selector (mobile) */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-xl font-semibold text-zinc-50 tracking-tight">
              {selectedDevice || datasetName}
            </h2>
            {selectedDevice && <RiskBadgeInline deviceName={selectedDevice} datasetId={datasetId} />}
            {summary?.date_range && (
              <span className="text-xs font-mono text-zinc-500">
                {summary.date_range.start} to {summary.date_range.end}
              </span>
            )}
            {selectedDevice && (
              <button
                onClick={() => setSelectedDevice(null)}
                className="text-[10px] font-mono text-teal-400 hover:text-teal-300 transition-colors"
                data-testid="clear-device-filter"
              >
                View All Devices
              </button>
            )}
          </div>
          <DateRangeFilter dateRange={dateRange} onDateRangeChange={setDateRange} />
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm"
              className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
              onClick={() => {
                const reportTimezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
                const url = `${API}/reports/export?dataset_id=${datasetId}&format=csv&timezone_name=${reportTimezone}${selectedDevice ? `&device_name=${encodeURIComponent(selectedDevice)}` : ''}`;
                downloadReport(url, "coldchain-report.csv").catch(() => toast.error("CSV export failed"));
              }}
              data-testid="export-csv-btn">
              <FileArrowDown size={14} className="mr-1" />CSV
            </Button>
            <Button variant="outline" size="sm"
              className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
              onClick={() => {
                const reportTimezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
                const url = `${API}/reports/export?dataset_id=${datasetId}&format=pdf&timezone_name=${reportTimezone}${selectedDevice ? `&device_name=${encodeURIComponent(selectedDevice)}` : ''}`;
                downloadReport(url, "coldchain-report.pdf").catch(() => toast.error("PDF export failed"));
              }}
              data-testid="export-pdf-btn">
              <FileArrowDown size={14} className="mr-1" />PDF
            </Button>
          </div>
        </div>

        {/* KPI Row */}
        <div className="animate-fade-up">
          <KPICards summary={summary} />
        </div>

        {/* Middle Row: Chart + AI Insights + Risk */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="lg:col-span-3 animate-fade-up stagger-1">
            <TemperatureChart trends={filteredTrends} />
          </div>
          <div className="lg:col-span-1 space-y-4 animate-fade-up stagger-2">
            {selectedDevice && <RiskPredictionPanel deviceName={selectedDevice} datasetId={datasetId} />}
            <AIInsightsPanel datasetId={datasetId} />
          </div>
        </div>

        {/* Bottom Row: Map + Driver + Seal | Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6">
          <div className="lg:col-span-2 space-y-4 animate-fade-up stagger-3">
            <DeviceMap events={filteredEvents} />
            <DriverBehavior datasetId={datasetId} />
          </div>
          <div className="lg:col-span-2 space-y-4 animate-fade-up stagger-4">
            <SealIntegrityTimeline events={filteredEvents} />
            <AlertHistory alerts={filteredAlerts} />
          </div>
        </div>
      </div>
    </div>
  );
}
